import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    credits: 3,
    dailyLimit: 0,
    color: '#475569',
    badge: null,
    features: [
      { text:'3 Free Credits',                ok:true  },
      { text:'Browse All Professors',          ok:true  },
      { text:'Access Scholarship Listings',    ok:true  },
      { text:'Basic Search Filters',           ok:true  },
      { text:'Limited Platform Access',        ok:true  },
      { text:'Reveal Professor Emails',        ok:false },
      { text:'Email Templates',               ok:false },
      { text:'Advanced Filters',              ok:false },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 4.99,
    yearlyPrice: 3.24,
    credits: 50,
    dailyLimit: 10,
    color: '#10b981',
    badge: null,
    features: [
      { text:'50 Credits / Month',             ok:true  },
      { text:'Reveal Professor Emails',        ok:true  },
      { text:'Advanced Professor Filters',     ok:true  },
      { text:'Save Favorite Professors',       ok:true  },
      { text:'Email Support',                  ok:true  },
      { text:'Email Templates',               ok:false },
      { text:'1-on-1 Consultancy',            ok:false },
      { text:'Scholarship Application Guides',ok:false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 9.99,
    yearlyPrice: 6.49,
    credits: 150,
    dailyLimit: 20,
    color: '#6366f1',
    badge: 'Most Popular',
    features: [
      { text:'150 Credits / Month',            ok:true  },
      { text:'All Premium Filters',            ok:true  },
      { text:'All Email Templates',            ok:true  },
      { text:'Professor Outreach Resources',   ok:true  },
      { text:'Application Strategy Resources', ok:true  },
      { text:'Priority Support',               ok:true  },
      { text:'1-on-1 Consultancy',            ok:false },
      { text:'Scholarship Documents Guide',   ok:false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    monthlyPrice: 19.99,
    yearlyPrice: 12.99,
    credits: 1000,
    dailyLimit: 40,
    color: '#f59e0b',
    badge: 'All Inclusive',
    features: [
      { text:'1000 Credits / Month',           ok:true  },
      { text:'Complete Scholarship Assistance',ok:true  },
      { text:'1-on-1 Consultancy Support',     ok:true  },
      { text:'CV & SOP Review',               ok:true  },
      { text:'University Shortlisting Help',   ok:true  },
      { text:'Visa Application Guidance',      ok:true  },
      { text:'WhatsApp Priority Support',      ok:true  },
      { text:'Form Filling Help',              ok:true  },
      { text:'Full Admission Guidance',        ok:true  },
      { text:'Scholarship Documents Guide',    ok:true  },
    ],
  },
];

function isPakistaniUser() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Karachi';
  } catch {
    return false;
  }
}

export default function UpgradeModal({ onClose }) {
  const { user, token } = useAuth();
  const [billing,  setBilling]  = useState('yearly');   // ← default yearly
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState(null);

  const getPrice = (plan) => {
    if (plan.monthlyPrice === 0) return '$0';
    const p = billing === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
    return `$${p.toFixed(2)}`;
  };

  const getSaving = (plan) => {
    if (!plan.monthlyPrice) return null;
    const save = ((plan.monthlyPrice - plan.yearlyPrice) * 12).toFixed(0);
    return `Save $${save}/yr`;
  };

  const handleUpgrade = async (planId) => {
    if (!user)             { onClose(); return; }
    if (planId === 'free') { onClose(); return; }

    setSelected(planId);
    setLoading(true);

    try {
      const isPK     = isPakistaniUser();
      const endpoint = isPK
        ? `${API}/payments/safepay/checkout`
        : `${API}/payments/lemonsqueezy/checkout`;

      const res = await fetch(endpoint, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ planId, billing }),
      });

      const data = await res.json();

      if (!res.ok || !data.checkoutUrl) {
        toast.error(data.error || 'Checkout failed. Try again.');
        setLoading(false);
        setSelected(null);
        return;
      }

      window.location.href = data.checkoutUrl;

    } catch (err) {
      console.error('Upgrade error:', err);
      toast.error('Something went wrong. Try again.');
      setLoading(false);
      setSelected(null);
    }
  };

  return (
    <div
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:'12px',overflowY:'auto'}}
      onClick={onClose}
    >
      <div
        style={{background:'#0a0f1a',borderRadius:'20px',padding:'24px 24px 20px',width:'100%',maxWidth:'860px',border:'1px solid rgba(255,255,255,0.08)'}}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{textAlign:'center',marginBottom:'16px'}}>
          <h2 style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'22px',fontWeight:'700',color:'#f1f5f9',margin:'0 0 4px'}}>
            Choose Your Plan
          </h2>
          <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#64748b',margin:0}}>
            Reveal professor emails, unlock templates and get scholarship guidance
          </p>
        </div>

        {/* Billing Toggle */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',marginBottom:'14px'}}>
          <span style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:billing==='monthly'?'#f1f5f9':'#64748b',fontWeight:billing==='monthly'?'600':'400'}}>
            Monthly
          </span>
          <div
            onClick={() => setBilling(b => b==='monthly'?'yearly':'monthly')}
            style={{width:'42px',height:'24px',borderRadius:'12px',background:billing==='yearly'?'#6366f1':'#334155',cursor:'pointer',position:'relative',transition:'background 0.2s'}}
          >
            <div style={{position:'absolute',top:'3px',left:billing==='yearly'?'21px':'3px',width:'18px',height:'18px',borderRadius:'50%',background:'#fff',transition:'left 0.2s'}}/>
          </div>
          <span style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:billing==='yearly'?'#f1f5f9':'#64748b',fontWeight:billing==='yearly'?'600':'400'}}>
            Yearly
          </span>
          {billing === 'yearly' && (
            <span style={{fontFamily:'Inter,sans-serif',fontSize:'11px',fontWeight:'700',background:'rgba(16,185,129,0.15)',color:'#10b981',padding:'2px 8px',borderRadius:'20px',border:'1px solid rgba(16,185,129,0.3)'}}>
              35% OFF
            </span>
          )}
        </div>

        {/* Gateway info */}
        <div style={{textAlign:'center',marginBottom:'16px'}}>
          <span style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569'}}>
            {isPakistaniUser()
              ? '🇵🇰 Paying via Safepay — PKR accepted'
              : '🌍 Paying via Lemon Squeezy — All cards accepted'}
          </span>
        </div>

        {/* Plan Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px',marginBottom:'16px'}}>
          {PLANS.map(plan => {
            const isCurrent = user?.plan === plan.id;
            const isLoading = loading && selected === plan.id;

            return (
              <div
                key={plan.id}
                style={{
                  background: plan.id==='pro'?'rgba(99,102,241,0.08)':'rgba(255,255,255,0.02)',
                  borderRadius:'14px',
                  padding:'16px 14px',
                  border: isCurrent
                    ? `2px solid ${plan.color}`
                    : plan.id==='pro'
                    ? '2px solid #6366f1'
                    : '1px solid rgba(255,255,255,0.08)',
                  position:'relative',
                  display:'flex',
                  flexDirection:'column',
                }}
              >
                {plan.badge && (
                  <div style={{position:'absolute',top:'-10px',left:'50%',transform:'translateX(-50%)',background:plan.color,color:'#fff',fontFamily:'Inter,sans-serif',fontSize:'10px',fontWeight:'700',padding:'2px 10px',borderRadius:'20px',whiteSpace:'nowrap'}}>
                    {plan.badge}
                  </div>
                )}

                {isCurrent && (
                  <div style={{position:'absolute',top:'-10px',right:'10px',background:plan.color,color:'#fff',fontFamily:'Inter,sans-serif',fontSize:'9px',fontWeight:'700',padding:'2px 8px',borderRadius:'20px'}}>
                    Current
                  </div>
                )}

                {/* Plan name */}
                <div style={{fontFamily:'Inter,sans-serif',fontSize:'11px',fontWeight:'600',color:'#64748b',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.5px'}}>
                  {plan.name}
                </div>

                {/* Price */}
                <div style={{marginBottom:'2px'}}>
                  <span style={{fontFamily:'Space Grotesk,sans-serif',fontSize:'26px',fontWeight:'700',color:plan.color}}>
                    {getPrice(plan)}
                  </span>
                  {plan.monthlyPrice > 0 && (
                    <span style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#475569'}}>/mo</span>
                  )}
                </div>

                {/* Yearly saving */}
                {billing === 'yearly' && plan.monthlyPrice > 0 && (
                  <div style={{fontFamily:'Inter,sans-serif',fontSize:'10px',color:'#10b981',fontWeight:'600',marginBottom:'2px'}}>
                    {getSaving(plan)}
                  </div>
                )}

                {/* Credits */}
                <div style={{fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#818cf8',fontWeight:'600',marginBottom:'10px'}}>
                  ⚡ {billing==='yearly'&&plan.monthlyPrice>0
                    ? `${plan.credits*12} credits/yr`
                    : `${plan.credits} credits/mo`}
                </div>

                {/* Features */}
                <div style={{flex:1,marginBottom:'14px'}}>
                  {plan.features.map((f,i) => (
                    <div key={i} style={{display:'flex',alignItems:'flex-start',gap:'6px',padding:'3px 0',fontFamily:'Inter,sans-serif',fontSize:'10.5px',color:f.ok?'#94a3b8':'#334155',lineHeight:'1.4'}}>
                      <span style={{color:f.ok?plan.color:'#334155',flexShrink:0,marginTop:'1px',fontSize:'11px'}}>
                        {f.ok ? '✓' : '✕'}
                      </span>
                      <span style={{textDecoration:f.ok?'none':'line-through'}}>{f.text}</span>
                    </div>
                  ))}
                </div>

                {/* Button */}
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={isCurrent || isLoading}
                  style={{
                    width:'100%',
                    padding:'9px',
                    background: isCurrent
                      ? 'rgba(255,255,255,0.04)'
                      : plan.id==='pro'
                      ? '#6366f1'
                      : `${plan.color}22`,
                    border:`1px solid ${isCurrent?'rgba(255,255,255,0.08)':plan.color}`,
                    borderRadius:'9px',
                    color: isCurrent?'#334155':plan.id==='pro'?'#fff':plan.color,
                    fontFamily:'Inter,sans-serif',
                    fontSize:'12px',
                    fontWeight:'600',
                    cursor: isCurrent?'not-allowed':'pointer',
                    transition:'all 0.2s',
                  }}
                  onMouseEnter={e=>{if(!isCurrent&&!isLoading)e.currentTarget.style.opacity='0.85';}}
                  onMouseLeave={e=>{e.currentTarget.style.opacity='1';}}
                >
                  {isLoading
                    ? '⏳ Redirecting...'
                    : isCurrent
                    ? 'Current Plan'
                    : plan.monthlyPrice===0
                    ? 'Free Plan'
                    : `Get ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          style={{width:'100%',padding:'10px',background:'transparent',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'10px',color:'#475569',fontFamily:'Inter,sans-serif',fontSize:'13px',cursor:'pointer'}}
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
}